import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { apiClient } from "@/config/api";
import { handleApiError } from "@/utils/functions";

import {
  DocusignCredentialsStatusResponse,
  DocusignErrorBody,
  DocusignTemplate,
  DocusignTemplateInput,
  SaveSignedDocumentBody,
} from "./types";
import { DOCUSIGN_CONNECTION_STATUS } from "./constants";

// Get document templates for a specific property
export const getPropertyDocumentTemplates = createAsyncThunk(
  "/property/:propertyId/docusign-templates",
  async (propertyId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        `/property/${propertyId}/docusign-templates`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      const data = response.data?.data;
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.templates)) return data.templates;
      return rejectWithValue("Invalid response format from API");
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            `Failed to fetch document templates: ${error.message}`
        );
      }
      return rejectWithValue("Failed to fetch document templates");
    }
  }
);

// Generate signing URL for a specific template
export const getDocusignTemplateUrl = createAsyncThunk(
  "/property/:propertyId/docusign-templates/:templateId/signing-url",
  async (
    { propertyId, templateId }: { propertyId: string; templateId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get(
        `/property/${propertyId}/docusign-templates/${templateId}/signing-url`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      const url: string | undefined =
        response.data?.data?.signingUrl ?? response.data?.data?.url;
      if (url) return url;
      return rejectWithValue("Invalid response format from API");
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            `Failed to initialize signing session: ${error.message}`
        );
      }
      return rejectWithValue("Failed to initialize signing session");
    }
  }
);

// Save signed document after Docusign callback
export const saveSignedDocumentOnBE = createAsyncThunk(
  "/property/:propertyId/docusign/:envelopeId/save",
  async (
    { propertyId, envelopeId, userId, type }: SaveSignedDocumentBody,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get(
        `/property/${propertyId}/docusign/${envelopeId}/save`,
        {
          params: {
            userId,
            type,
          },
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            `Failed to save signed document: ${error.message}`
        );
      }
      return rejectWithValue("Failed to save signed document");
    }
  }
);

// Keep old function name as alias for backward compatibility during transition
export const getOnboardingDocuments = getPropertyDocumentTemplates;

// =====================================================================
// Organization-level Docusign credentials & template management
// =====================================================================

export const getDocusignCredentials = createAsyncThunk(
  "organizations/docusign-credentials/get",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        "/organizations/docusign-credentials"
      );
      const data = response.data?.data as
        | DocusignCredentialsStatusResponse
        | undefined;
      return data ?? { status: DOCUSIGN_CONNECTION_STATUS.NOT_CONNECTED };
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Failed to load Docusign credentials")
      );
    }
  }
);

export const getDocusignConnectUrl = createAsyncThunk(
  "organizations/docusign-connect/get",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/organizations/docusign-connect");
      return response.data.data.url as string;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Failed to start the Docusign connection")
      );
    }
  }
);

export const deleteDocusignCredentials = createAsyncThunk(
  "organizations/docusign-credentials/delete",
  async (_: void, { rejectWithValue }) => {
    try {
      await apiClient.delete("/organizations/docusign-credentials");
      return true;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Failed to disconnect Docusign")
      );
    }
  }
);

export const getDocusignTemplates = createAsyncThunk(
  "organizations/docusign-templates/get",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/organizations/docusign-templates");
      return (response.data?.data ?? []) as DocusignTemplate[];
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Failed to load Docusign templates")
      );
    }
  }
);

export const addDocusignTemplate = createAsyncThunk(
  "organizations/docusign-templates/add",
  async (body: DocusignTemplateInput, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        "/organizations/docusign-templates",
        body
      );
      return response.data.data as DocusignTemplate;
    } catch (error) {
      // 409 duplicate / 404 credentials-or-template / 422 bad body / 500 unavailable
      // all carry a structured { code, message } so the modal can map nicely.
      if (axios.isAxiosError(error)) {
        const errBody = error.response?.data?.error as
          | DocusignErrorBody
          | undefined;
        return rejectWithValue(errBody ?? {});
      }
      return rejectWithValue({
        message: handleApiError(error, "Failed to add Docusign template"),
      } as DocusignErrorBody);
    }
  }
);

export const deleteDocusignTemplate = createAsyncThunk(
  "organizations/docusign-templates/delete",
  async (templateId: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/organizations/docusign-templates/${templateId}`);
      return templateId;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Failed to remove Docusign template")
      );
    }
  }
);

